// Daily check-in (7-day streak, bonus chest on day 7) and challenges: Daily (3 a day from a pool of 50), Weekly (3 a
// week, Monday to Sunday) and Seasonal (a fixed list for the whole season). All shown on the menus' REWARDS screen.
// State lives on the profile (`profile.daily`), persisted like the shop does: straight into localStorage
// 'slimeduel.profile', touching only the keys it owns so a stale in-memory profile can't undo it.
import { saveCloudProgress } from '../auth/account.js';

const PROFILE_KEY = 'slimeduel.profile';
export const CHALLENGES_PER_DAY = 3;

// ---- check-in: day 1..7 of the streak cycle; day 7 also drops a loot crate
export const CHECKIN_REWARDS = [
  { day: 1, gems: 50 },
  { day: 2, gems: 75 },
  { day: 3, gems: 100 },
  { day: 4, gems: 125 },
  { day: 5, gems: 150 },
  { day: 6, gems: 200 },
  { day: 7, gems: 300, crates: 1 },
];

export const TIER_GEMS = { easy: 40, medium: 70, hard: 120 };
export const WEEKLY_PER_WEEK = 3;
export const SEASON_ID = 's1';
export const SEASON_NAME = 'SEASON 1';
// gems by challenge set and tier (seasonal hard ones also drop a loot crate)
export const SET_GEMS = { daily: TIER_GEMS, weekly: { easy: 150, medium: 250, hard: 400 }, season: { easy: 500, medium: 900, hard: 1500 } };
export const SET_CRATES = { season: { hard: 1 } };
export const SETS = ['daily', 'weekly', 'season'];

const CLOSE = ['roller', 'brush', 'blade', 'mitts', 'bucket', 'brolly', 'pumpy', 'scatter'];
const LONG = ['charger', 'awp', 'bow', 'blaster'];
const AUTO = ['shooter', 'pulse_rifle', 'twins', 'spinner', 'dregs'];

export const MODE_LABEL = { any: 'ANY MODE', turf: 'TURF RIOT', zones: 'ZONE CONTROL', duel: '1v1 DUEL', deathmatch: 'DEATHMATCH', boss: 'BOSS BATTLE' };

// A challenge counts a metric from the local player's finished match:
//   metric  kills | turf | specials | zoneTurf | bossDmg | weakHits | played | won
//   agg     'sum' adds up across matches, 'max' keeps the best single match
//   mode    which mode counts ('any' = all)    cond  extra rule: 'won' | 'noDeath' | 'close' | 'long' | 'auto'
const C = (id, mode, metric, agg, target, text, tier, cond) => ({ id, mode, metric, agg, target, text, tier, cond: cond || null });
export const CHALLENGE_POOL = [
  // any mode — kills
  C('any_kills_10', 'any', 'kills', 'sum', 10, 'Splat 10 opponents', 'easy'),
  C('any_kills_25', 'any', 'kills', 'sum', 25, 'Splat 25 opponents', 'medium'),
  C('any_kills_50', 'any', 'kills', 'sum', 50, 'Splat 50 opponents', 'hard'),
  C('any_kills_best_6', 'any', 'kills', 'max', 6, 'Get 6 splats in a single match', 'easy'),
  C('any_kills_best_10', 'any', 'kills', 'max', 10, 'Get 10 splats in a single match', 'medium'),
  C('any_kills_best_15', 'any', 'kills', 'max', 15, 'Get 15 splats in a single match', 'hard'),
  // any mode — turf
  C('any_turf_2500', 'any', 'turf', 'sum', 2500, 'Slime 2,500 turf in total', 'easy'),
  C('any_turf_5000', 'any', 'turf', 'sum', 5000, 'Slime 5,000 turf in total', 'medium'),
  C('any_turf_9000', 'any', 'turf', 'sum', 9000, 'Slime 9,000 turf in total', 'hard'),
  C('any_turf_best_800', 'any', 'turf', 'max', 800, 'Slime 800 turf in a single match', 'easy'),
  C('any_turf_best_1400', 'any', 'turf', 'max', 1400, 'Slime 1,400 turf in a single match', 'medium'),
  // any mode — matches, wins, specials
  C('any_play_2', 'any', 'played', 'sum', 2, 'Play 2 matches', 'easy'),
  C('any_play_4', 'any', 'played', 'sum', 4, 'Play 4 matches', 'medium'),
  C('any_win_1', 'any', 'won', 'sum', 1, 'Win a match', 'easy'),
  C('any_win_3', 'any', 'won', 'sum', 3, 'Win 3 matches', 'hard'),
  C('any_special_3', 'any', 'specials', 'sum', 3, 'Use your special 3 times', 'easy'),
  C('any_special_8', 'any', 'specials', 'sum', 8, 'Use your special 8 times', 'medium'),
  C('any_nodeath_win', 'any', 'won', 'sum', 1, 'Win a match without being splatted', 'hard', 'noDeath'),
  C('any_nodeath_play', 'any', 'played', 'sum', 2, 'Finish 2 matches without being splatted', 'hard', 'noDeath'),
  // weapon style
  C('close_kills_10', 'any', 'kills', 'sum', 10, 'Splat 10 opponents with a close-range weapon', 'medium', 'close'),
  C('close_win_1', 'any', 'won', 'sum', 1, 'Win a match with a close-range weapon', 'medium', 'close'),
  C('long_kills_10', 'any', 'kills', 'sum', 10, 'Splat 10 opponents with a long-range weapon', 'medium', 'long'),
  C('long_win_1', 'any', 'won', 'sum', 1, 'Win a match with a long-range weapon', 'medium', 'long'),
  C('auto_kills_15', 'any', 'kills', 'sum', 15, 'Splat 15 opponents with a rapid-fire weapon', 'medium', 'auto'),
  C('auto_turf_3000', 'any', 'turf', 'sum', 3000, 'Slime 3,000 turf with a rapid-fire weapon', 'medium', 'auto'),
  // turf riot
  C('turf_win_1', 'turf', 'won', 'sum', 1, 'Win a Turf Riot', 'easy'),
  C('turf_win_2', 'turf', 'won', 'sum', 2, 'Win 2 Turf Riots', 'hard'),
  C('turf_play_2', 'turf', 'played', 'sum', 2, 'Play 2 Turf Riots', 'easy'),
  C('turf_ink_2000', 'turf', 'turf', 'sum', 2000, 'Slime 2,000 turf in Turf Riot', 'easy'),
  C('turf_ink_best_1200', 'turf', 'turf', 'max', 1200, 'Slime 1,200 turf in a single Turf Riot', 'medium'),
  C('turf_kills_12', 'turf', 'kills', 'sum', 12, 'Splat 12 opponents in Turf Riot', 'medium'),
  // zone control
  C('zones_win_1', 'zones', 'won', 'sum', 1, 'Win a Zone Control match', 'medium'),
  C('zones_play_2', 'zones', 'played', 'sum', 2, 'Play 2 Zone Control matches', 'easy'),
  C('zones_zink_400', 'zones', 'zoneTurf', 'sum', 400, 'Slime 400 turf on the live zone', 'medium'),
  C('zones_zink_best_300', 'zones', 'zoneTurf', 'max', 300, 'Slime 300 zone turf in a single match', 'hard'),
  C('zones_kills_10', 'zones', 'kills', 'sum', 10, 'Splat 10 opponents in Zone Control', 'medium'),
  // duel
  C('duel_play_2', 'duel', 'played', 'sum', 2, 'Play 2 Duels', 'easy'),
  C('duel_win_1', 'duel', 'won', 'sum', 1, 'Win a 1v1 Duel', 'medium'),
  C('duel_win_3', 'duel', 'won', 'sum', 3, 'Win 3 Duels', 'hard'),
  C('duel_kills_8', 'duel', 'kills', 'sum', 8, 'Splat your rival 8 times in Duels', 'medium'),
  C('duel_nodeath', 'duel', 'won', 'sum', 1, 'Win a Duel without being splatted', 'hard', 'noDeath'),
  // deathmatch
  C('dm_play_2', 'deathmatch', 'played', 'sum', 2, 'Play 2 Deathmatches', 'easy'),
  C('dm_win_1', 'deathmatch', 'won', 'sum', 1, 'Finish 1st in a Deathmatch', 'hard'),
  C('dm_kills_15', 'deathmatch', 'kills', 'sum', 15, 'Get 15 splats in Deathmatch', 'medium'),
  C('dm_kills_best_8', 'deathmatch', 'kills', 'max', 8, 'Get 8 splats in a single Deathmatch', 'medium'),
  C('dm_special_4', 'deathmatch', 'specials', 'sum', 4, 'Use your special 4 times in Deathmatch', 'easy'),
  // boss battle
  C('boss_play_1', 'boss', 'played', 'sum', 1, 'Fight HULLBREAKER in a Boss Battle', 'easy'),
  C('boss_win_1', 'boss', 'won', 'sum', 1, 'Defeat the boss in a Boss Battle', 'hard'),
  C('boss_dmg_2000', 'boss', 'bossDmg', 'sum', 2000, 'Deal 2,000 damage to the boss', 'medium'),
  C('boss_weak_10', 'boss', 'weakHits', 'sum', 10, 'Land 10 weak-point hits on the boss', 'medium'),
];

// a week's worth: bigger numbers, three a week
export const WEEKLY_POOL = [
  C('w_kills_60', 'any', 'kills', 'sum', 60, 'Splat 60 opponents', 'medium'),
  C('w_kills_120', 'any', 'kills', 'sum', 120, 'Splat 120 opponents', 'hard'),
  C('w_turf_15000', 'any', 'turf', 'sum', 15000, 'Slime 15,000 turf', 'medium'),
  C('w_turf_30000', 'any', 'turf', 'sum', 30000, 'Slime 30,000 turf', 'hard'),
  C('w_play_10', 'any', 'played', 'sum', 10, 'Play 10 matches', 'easy'),
  C('w_win_6', 'any', 'won', 'sum', 6, 'Win 6 matches', 'medium'),
  C('w_win_12', 'any', 'won', 'sum', 12, 'Win 12 matches', 'hard'),
  C('w_special_20', 'any', 'specials', 'sum', 20, 'Use your special 20 times', 'easy'),
  C('w_turf_win_4', 'turf', 'won', 'sum', 4, 'Win 4 Turf Riots', 'medium'),
  C('w_zones_play_4', 'zones', 'played', 'sum', 4, 'Play 4 Zone Control matches', 'easy'),
  C('w_zones_zink_1500', 'zones', 'zoneTurf', 'sum', 1500, 'Slime 1,500 turf on the live zone', 'medium'),
  C('w_duel_win_4', 'duel', 'won', 'sum', 4, 'Win 4 Duels', 'medium'),
  C('w_dm_kills_40', 'deathmatch', 'kills', 'sum', 40, 'Get 40 splats in Deathmatch', 'medium'),
  C('w_boss_win_2', 'boss', 'won', 'sum', 2, 'Defeat the boss twice', 'hard'),
  C('w_close_kills_30', 'any', 'kills', 'sum', 30, 'Splat 30 opponents with a close-range weapon', 'medium', 'close'),
  C('w_long_kills_30', 'any', 'kills', 'sum', 30, 'Splat 30 opponents with a long-range weapon', 'medium', 'long'),
  C('w_best_kills_12', 'any', 'kills', 'max', 12, 'Get 12 splats in a single match', 'hard'),
];
// the whole season, all at once
export const SEASON_POOL = [
  C('s1_play_50', 'any', 'played', 'sum', 50, 'Play 50 matches this season', 'easy'),
  C('s1_turf_100k', 'any', 'turf', 'sum', 100000, 'Slime 100,000 turf this season', 'medium'),
  C('s1_kills_400', 'any', 'kills', 'sum', 400, 'Splat 400 opponents this season', 'hard'),
  C('s1_win_30', 'any', 'won', 'sum', 30, 'Win 30 matches this season', 'hard'),
];
const POOLS = { daily: CHALLENGE_POOL, weekly: WEEKLY_POOL, season: SEASON_POOL };

// ------------------------------------------------------------------ dates
export function dayKey(d = new Date()) {
  const m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
const dayNum = (key) => { const [y, m, d] = key.split('-').map(Number); return Math.round(Date.UTC(y, m - 1, d) / 86400000); };
const daysBetween = (a, b) => dayNum(b) - dayNum(a);
export function msToMidnight(d = new Date()) {
  const n = new Date(d); n.setHours(24, 0, 0, 0); return n - d;
}
/** The Monday that starts this week, as a dayKey (weeks run Monday to Sunday, local time). */
export function weekKey(d = new Date()) {
  const n = new Date(d); n.setHours(0, 0, 0, 0); n.setDate(n.getDate() - ((n.getDay() + 6) % 7)); return dayKey(n);
}
export function msToWeekEnd(d = new Date()) {
  const n = new Date(d); n.setHours(0, 0, 0, 0); n.setDate(n.getDate() - ((n.getDay() + 6) % 7) + 7); return n - d;
}

// ------------------------------------------------------------------ state
const poolById = new Map([...CHALLENGE_POOL, ...WEEKLY_POOL, ...SEASON_POOL].map((c) => [c.id, c]));
export const challengeDef = (id) => poolById.get(id) || null;

function pickChallenges(prevIds = [], from = CHALLENGE_POOL, count = CHALLENGES_PER_DAY) {
  // random, but spread across modes: never two from the same mode (any-mode may repeat once) and never last time's again
  const picked = [];
  const pool = from.filter((c) => !prevIds.includes(c.id));
  const shuffle = (a) => { for (let i = a.length - 1; i > 0; i--) { const j = (Math.random() * (i + 1)) | 0; [a[i], a[j]] = [a[j], a[i]]; } return a; };
  const shuffled = shuffle([...pool]);
  const usedModes = new Map();
  for (const c of shuffled) {
    if (picked.length >= count) break;
    const n = usedModes.get(c.mode) || 0;
    if (n >= (c.mode === 'any' ? 2 : 1)) continue;
    usedModes.set(c.mode, n + 1);
    picked.push(c);
  }
  for (const c of shuffled) { if (picked.length >= count) break; if (!picked.includes(c)) picked.push(c); }
  return picked;
}

function readProfile() { try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || '{}'); } catch { return {}; } }
function persist(prof) {
  try {
    const cur = readProfile();
    for (const k of ['daily', 'coins', 'lootBoxes']) if (prof[k] !== undefined) cur[k] = prof[k];
    localStorage.setItem(PROFILE_KEY, JSON.stringify(cur));
  } catch { /* private mode */ }
  try { saveCloudProgress(); } catch { /* no account */ }
}

/** Make prof.daily current for today: rolls the challenges over at midnight. Returns it. */
export function ensureDaily(prof, now = new Date()) {
  const today = dayKey(now);
  let d = prof.daily;
  if (!d || typeof d !== 'object') d = prof.daily = { streak: 0, lastCheckin: null, challengeDay: null, challenges: [], done: 0 };
  if (d.challengeDay !== today || !Array.isArray(d.challenges) || d.challenges.length !== CHALLENGES_PER_DAY || d.challenges.some((q) => !poolById.has(q.id))) {
    const prev = (d.challenges || []).map((q) => q.id);
    d.challenges = pickChallenges(prev).map((c) => ({ id: c.id, progress: 0, claimed: false }));
    d.challengeDay = today;
    persist(prof);
  }
  // weekly: three new ones every Monday · seasonal: the season's fixed list, kept until the season changes
  const wk = weekKey(now);
  if (!d.weekly || d.weekly.key !== wk || !Array.isArray(d.weekly.challenges) || d.weekly.challenges.some((q) => !poolById.has(q.id))) {
    const prev = ((d.weekly && d.weekly.challenges) || []).map((q) => q.id);
    d.weekly = { key: wk, challenges: pickChallenges(prev, WEEKLY_POOL, WEEKLY_PER_WEEK).map((c) => ({ id: c.id, progress: 0, claimed: false })) };
    persist(prof);
  }
  if (!d.season || d.season.key !== SEASON_ID || !Array.isArray(d.season.challenges) || d.season.challenges.length !== SEASON_POOL.length) {
    const old = d.season && d.season.key === SEASON_ID ? d.season.challenges || [] : [];
    d.season = { key: SEASON_ID, challenges: SEASON_POOL.map((c) => old.find((q) => q.id === c.id) || { id: c.id, progress: 0, claimed: false }) };
    persist(prof);
  }
  return d;
}

/** The challenge list for a set: 'daily' | 'weekly' | 'season'. */
export function challengesOf(prof, set = 'daily', now = new Date()) {
  const d = ensureDaily(prof, now);
  return set === 'weekly' ? d.weekly.challenges : set === 'season' ? d.season.challenges : d.challenges;
}
export function rewardOf(set, def) {
  return { gems: (SET_GEMS[set] || TIER_GEMS)[def.tier] || 40, crates: (SET_CRATES[set] && SET_CRATES[set][def.tier]) || 0 };
}

// ------------------------------------------------------------------ check-in
/** Where the streak stands today: { canClaim, streak (as it will be after claiming), day (1..7 of today's/next reward), reward } */
export function checkinState(prof, now = new Date()) {
  const d = ensureDaily(prof, now);
  const today = dayKey(now);
  const claimedToday = d.lastCheckin === today;
  let streak = d.streak || 0;
  // a missed day breaks the streak
  if (d.lastCheckin && !claimedToday && daysBetween(d.lastCheckin, today) > 1) streak = 0;
  const nextStreak = claimedToday ? streak : streak + 1;
  const day = ((nextStreak - 1) % 7) + 1;
  return { canClaim: !claimedToday, claimedToday, streak, nextStreak, day, claimedDays: claimedToday ? day : day - 1, reward: CHECKIN_REWARDS[day - 1] };
}

/** Claim today's check-in. Returns the reward { day, gems, crates } or null if already claimed. */
export function claimCheckin(prof, now = new Date()) {
  const st = checkinState(prof, now);
  if (!st.canClaim) return null;
  const d = prof.daily;
  d.streak = st.nextStreak;
  d.lastCheckin = dayKey(now);
  d.best = Math.max(d.best || 0, d.streak);
  const r = st.reward;
  prof.coins = (prof.coins || 0) + r.gems;
  if (r.crates) prof.lootBoxes = (prof.lootBoxes || 0) + r.crates;
  persist(prof);
  return { day: r.day, gems: r.gems, crates: r.crates || 0, streak: d.streak };
}

// ------------------------------------------------------------------ challenges
function weaponOk(cond, weaponId) {
  if (cond === 'close') return CLOSE.includes(weaponId);
  if (cond === 'long') return LONG.includes(weaponId);
  if (cond === 'auto') return AUTO.includes(weaponId);
  return true;
}

/**
 * Feed a finished match in. m = { mode, won, kills, deaths, turf, specials, zoneTurf, bossDmg, weakHits, weapon }.
 * Returns the challenges that just completed ([{ def, q }]).
 */
export function recordMatch(prof, m, now = new Date()) {
  ensureDaily(prof, now);
  const vals = { kills: m.kills || 0, turf: m.turf || 0, specials: m.specials || 0, zoneTurf: m.zoneTurf || 0, bossDmg: m.bossDmg || 0, weakHits: m.weakHits || 0, played: 1, won: m.won ? 1 : 0 };
  const finished = [];
  for (const set of SETS) for (const q of challengesOf(prof, set, now)) {
    const def = poolById.get(q.id);
    if (!def || q.claimed || q.progress >= def.target) continue;
    if (def.mode !== 'any' && def.mode !== m.mode) continue;
    if (def.cond === 'noDeath' && (m.deaths || 0) > 0) continue;
    if (!weaponOk(def.cond, m.weapon)) continue;
    const v = vals[def.metric] || 0;
    const before = q.progress;
    q.progress = Math.min(def.target, def.agg === 'max' ? Math.max(q.progress, v) : q.progress + v);
    if (before < def.target && q.progress >= def.target) finished.push({ def, q, set });
  }
  persist(prof);
  return finished;
}

/** Collect a completed challenge's reward (set: 'daily' | 'weekly' | 'season'). Returns the gems paid, or 0. */
export function claimChallenge(prof, index, now = new Date(), set = 'daily') {
  const q = challengesOf(prof, set, now)[index];
  const def = q && poolById.get(q.id);
  if (!def || q.claimed || q.progress < def.target) return 0;
  q.claimed = true;
  const r = rewardOf(set, def);
  prof.coins = (prof.coins || 0) + r.gems;
  if (r.crates) prof.lootBoxes = (prof.lootBoxes || 0) + r.crates;
  persist(prof);
  return r.gems;
}

/** Finished, unclaimed challenges in a set. */
export function readyCount(prof, set = 'daily', now = new Date()) {
  return challengesOf(prof, set, now).filter((q) => { const def = poolById.get(q.id); return def && !q.claimed && q.progress >= def.target; }).length;
}
/** How many things are waiting on the player: an unclaimed check-in plus finished, unclaimed challenges (every set). */
export function pendingCount(prof, now = new Date()) {
  return SETS.reduce((n, set) => n + readyCount(prof, set, now), 0) + (checkinState(prof, now).canClaim ? 1 : 0);
}

/** Stats of the local actor → recordMatch's input. */
export function matchFromActor(local, mode, won) {
  const s = (local && local.stats) || {};
  return { mode, won: !!won, kills: s.splats || 0, deaths: s.deaths || 0, turf: Math.round(s.turf || 0), specials: s.specials || 0, zoneTurf: Math.round(s.zoneTurf || 0), bossDmg: Math.round(s.bossDmg || 0), weakHits: s.weakHits || 0, weapon: local && local.weaponId };
}
